// Manage Chromatography — port of the nos-housekeeping-features design.
//
// Each org user pre-registers the chromatography configurations (physical
// machine + dialled-in method + alias) they actually run. The alias is what
// shows up everywhere a chromatogram is uploaded / displayed downstream.
//
// Page composition mirrors the design 1:1:
//   - Topbar with "Manage Chromatography" + Configs / Chromatograms tiles
//     and a "Register configuration" CTA.
//   - Filter rail (vendor / platform / analyser / resolution / tags).
//   - Toolbar with search + "Default for new uploads" config picker.
//   - Orphan-chromatogram strip (assignment dropdown per row).
//   - Cards: clickable header, expanded body splits experiment-config detail
//     left and a stretchy alkane-ladder / chromatograms column right.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/Icon/Icon";
import {
  ANALYSER_TYPES,
  INSTRUMENT_VENDORS,
  findAnalyser,
  instrumentsKeys,
  useInstrumentsCatalogs,
} from "@/hooks/useInstruments";
import {
  archiveChromatogram,
  archiveTag,
  assignChromatogram,
  clearDefault,
  createTag,
  getDefault,
  listConfigChromatograms,
  listConfigLadders,
  listConfigs,
  listOrphans,
  listTags,
  renameTag,
  setDefault,
  type ConfigChromatogramRow,
  type LadderRow,
  type OrphanChromatogram,
  type TagDTO,
} from "@/services/instruments_api";
import { dtoToProfile } from "@/lib/instrumentsDto";
import type {
  AnalyserId,
  AnalyserResolution,
  ChromatogramFile,
  InstrumentPlatform,
  InstrumentProfile,
  InstrumentVendorId,
  OvenStep,
} from "@/utils/types/instrument";
import { AlkaneLadderEditor } from "./AlkaneLadderEditor";
import { ConfigPicker } from "./ConfigPicker";
import { InstrumentDrawer, type InstrumentDrawerState } from "./InstrumentDrawer";

const TODAY = new Date("2026-05-17");
// Brand-accurate vendor accents — Agilent corporate blue, Thermo Fisher red.
const vendorBrandAccent = (vendor: InstrumentVendorId | null | undefined): string => {
  if (vendor === "Agilent") return "#0096D6";
  if (vendor === "Thermofisher") return "#D71920";
  return "var(--ink-3)";
};

const relativeDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.round((TODAY.getTime() - d.getTime()) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.round(diff / 30)}mo ago`;
  return `${Math.round(diff / 365)}y ago`;
};

// Server's orphan-chromatogram row → the UI's ChromatogramFile shape that
// the OrphanStrip + per-card chromatogram list already render. Peak count
// and ingredient link aren't carried on the wire yet — the parser worker
// would populate metadata later; for now we surface 0 / null and let the
// row read as "uploaded, awaiting parse" via its assignment dropdown.
// Per-config chromatogram row → the UI's ChromatogramFile shape. id is
// the chromatogram_id (not the file_upload_id) so callers can fire
// archiveChromatogram directly.
function configChromaToFile(r: ConfigChromatogramRow): ChromatogramFile {
  const ext = (r.original_filename.split(".").pop() ?? "raw").toLowerCase();
  const format = (["raw", "mzml", "csv", "xlsx"].includes(ext)
    ? ext === "mzml"
      ? "mzML"
      : ext
    : "raw") as ChromatogramFile["format"];
  return {
    id: r.file_upload_id, // keep ChromaRow's id semantic stable across orphan + assigned views
    name: r.original_filename,
    format,
    peaks: 0,
    sizeMb: Math.round((r.size_bytes / (1024 * 1024)) * 10) / 10,
    uploadedAt: r.uploaded_at ?? r.created_at,
    ingredient: null,
  };
}

function orphanDtoToChromatogramFile(o: OrphanChromatogram): ChromatogramFile {
  const ext = (o.original_filename.split(".").pop() ?? "raw").toLowerCase();
  const format = (["raw", "mzml", "csv", "xlsx"].includes(ext)
    ? ext === "mzml"
      ? "mzML"
      : ext
    : "raw") as ChromatogramFile["format"];
  return {
    id: o.file_upload_id,
    name: o.original_filename,
    format,
    peaks: 0,
    sizeMb: Math.round((o.size_bytes / (1024 * 1024)) * 10) / 10,
    uploadedAt: o.uploaded_at ?? new Date(0).toISOString().slice(0, 10),
    ingredient: null,
  };
}

export const formatOven = (program: OvenStep[] | null | undefined): string => {
  if (!program || !program.length) return "—";
  const fmtTemp = (v: number | null) => (v == null ? "—" : `${v}°C`);
  const fmtRate = (v: number | null) => (v == null ? "—" : `${v}°/min`);
  const fmtHold = (v: number | null) => (v ? ` (${v}′)` : "");
  return program
    .map((s) =>
      s.kind === "initial"
        ? `${fmtTemp(s.temp)}${fmtHold(s.hold)}`
        : `${fmtRate(s.rate)} → ${fmtTemp(s.target)}${fmtHold(s.hold)}`,
    )
    .join(" → ");
};

type Filters = {
  vendor: InstrumentVendorId[];
  platform: InstrumentPlatform[];
  analyser: AnalyserId[];
  resolution: AnalyserResolution[];
  tag: string[];
};

const EMPTY_FILTERS: Filters = { vendor: [], platform: [], analyser: [], resolution: [], tag: [] };

export function InstrumentsPage() {
  const navigate = useNavigate();
  const { catalogsQ, findInstrumentCatalog } = useInstrumentsCatalogs();

  // Configs query — source of truth for the cards list. Local profiles state
  // mirrors the server response so existing mock-write handlers (save, delete,
  // assign orphan) still produce optimistic UI updates until F3/F6/F11 swap
  // those into real mutations.
  // `select` coerces non-array responses (HTML auth redirects, error
  // envelopes, stale persisted shapes) to []. Without this, a transient
  // non-array body crashes the entire page render via `.map is not a
  // function`. Real errors still propagate via queryQ.error.
  const configsQ = useQuery({
    queryKey: instrumentsKeys.configs,
    queryFn: listConfigs,
    select: (d) => (Array.isArray(d) ? d : []),
  });
  const [profiles, setProfiles] = useState<InstrumentProfile[]>([]);
  // F11: orphan chromatograms — file_uploads with kind='chromatogram',
  // status='uploaded', no chromatogram row yet. Mapped onto the UI's
  // ChromatogramFile shape so the existing OrphanStrip renders unchanged.
  const orphansQ = useQuery({
    queryKey: instrumentsKeys.orphans,
    queryFn: listOrphans,
    select: (d) => (Array.isArray(d) ? d : []),
  });
  const orphans = useMemo<ChromatogramFile[]>(
    () => (orphansQ.data ?? []).map(orphanDtoToChromatogramFile),
    [orphansQ.data],
  );

  // F8: tag library state — read from the server, write via mutations.
  // tagPool keeps the legacy `string[]` shape the drawer's TagEditor
  // expects; tagPoolMap is the lookup the drawer uses to resolve a
  // display_text back to a TagDTO (id + scope) when firing attach /
  // detach.
  const tagsQ = useQuery({
    queryKey: instrumentsKeys.tags,
    queryFn: listTags,
    select: (d) => (Array.isArray(d) ? d : []),
  });
  const tagPool = useMemo<string[]>(
    () => (tagsQ.data ?? []).map((t) => t.display_text),
    [tagsQ.data],
  );
  const tagPoolMap = useMemo<Map<string, TagDTO>>(() => {
    const m = new Map<string, TagDTO>();
    for (const t of tagsQ.data ?? []) m.set(t.display_text, t);
    return m;
  }, [tagsQ.data]);
  const tagIdToText = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const t of tagsQ.data ?? []) m.set(t.id, t.display_text);
    return m;
  }, [tagsQ.data]);

  // Hydrate profiles from server data. Joins config.tag_ids → display_text
  // via the tag pool; if the pool hasn't loaded yet, tags stay empty until
  // tagsQ resolves (re-runs via dep array).
  useEffect(() => {
    if (configsQ.data) {
      setProfiles(
        configsQ.data.map((dto) =>
          dtoToProfile(dto, {
            tags: dto.tag_ids
              .map((id) => tagIdToText.get(id))
              .filter((t): t is string => Boolean(t)),
          }),
        ),
      );
    }
  }, [configsQ.data, tagIdToText]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");

  // F7: default config picker. The server is the source of truth; the
  // picker reads `defaultId` from the query and writes via the mutations.
  // Picking "" (the implicit "no default" option in ConfigPicker) clears;
  // picking an id sets.
  const qc = useQueryClient();
  const defaultQ = useQuery({
    queryKey: instrumentsKeys.default,
    queryFn: getDefault,
  });
  const defaultId = defaultQ.data?.user_exp_config_id ?? "";
  const setDefaultM = useMutation({
    mutationFn: (id: string) => setDefault(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.default });
    },
  });
  const clearDefaultM = useMutation({
    mutationFn: () => clearDefault(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.default });
    },
  });
  const handleSetDefault = useCallback(
    (next: string | null) => {
      if (next == null || next === "") {
        clearDefaultM.mutate();
      } else {
        setDefaultM.mutate(next);
      }
    },
    [setDefaultM, clearDefaultM],
  );
  const defaultMutationPending = setDefaultM.isPending || clearDefaultM.isPending;

  const [drawer, setDrawer] = useState<InstrumentDrawerState | null>(null);

  // Org-scoped tag pool operations. Renaming / deleting a tag ripples across
  // both the shared pool and every configuration that carries it — same as
  // the inventory tag selectors.
  const getTagUsage = useCallback(
    (t: string) => profiles.filter((p) => (p.tags || []).includes(t)).length,
    [profiles],
  );

  // Library-level tag mutations. createTag is idempotent server-side
  // (lookup-then-insert on the partial unique index), so duplicate
  // additions are no-ops. rename / archive surface 409 / 404 via the
  // mutations' error state — the drawer's TagEditor doesn't currently
  // render those, so we only invalidate on success.
  const createTagM = useMutation({
    mutationFn: (display_text: string) => createTag(display_text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.tags });
    },
  });
  const renameTagM = useMutation({
    mutationFn: ({ tagId, display_text }: { tagId: string; display_text: string }) =>
      renameTag(tagId, display_text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.tags });
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    },
  });
  const archiveTagM = useMutation({
    mutationFn: (tagId: string) => archiveTag(tagId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.tags });
      // Server ripple-archives every live rel pointing at this tag,
      // so per-config tag lists need a fresh read.
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    },
  });

  const handleCreateTag = useCallback(
    (t: string) => {
      if (!t.trim()) return;
      if (tagPoolMap.has(t)) return;
      createTagM.mutate(t.trim());
    },
    [createTagM, tagPoolMap],
  );

  const handleRenameTag = useCallback(
    (oldT: string, newT: string) => {
      const tag = tagPoolMap.get(oldT);
      if (!tag || !newT.trim() || oldT === newT) return;
      renameTagM.mutate({ tagId: tag.id, display_text: newT.trim() });
      // Local filter state still references the old text — fix locally so
      // the rail facet doesn't lose its selection while the invalidation
      // round-trip resolves.
      setFilters((f) => ({ ...f, tag: f.tag.map((x) => (x === oldT ? newT.trim() : x)) }));
    },
    [renameTagM, tagPoolMap],
  );

  const handleDeleteTag = useCallback(
    (t: string) => {
      const tag = tagPoolMap.get(t);
      if (!tag) return;
      archiveTagM.mutate(tag.id);
      setFilters((f) => ({ ...f, tag: f.tag.filter((x) => x !== t) }));
    },
    [archiveTagM, tagPoolMap],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter((p) => {
      const inst = findInstrumentCatalog(p.instrumentId);
      const an = findAnalyser(inst?.analyser);
      if (q) {
        const hay = `${p.alias} ${inst?.label ?? ""} ${inst?.vendor ?? ""} ${p.notes ?? ""} ${(p.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.vendor.length && (!inst || !filters.vendor.includes(inst.vendor))) return false;
      if (filters.platform.length && (!inst || !filters.platform.includes(inst.platform))) return false;
      if (filters.analyser.length && (!inst || !filters.analyser.includes(inst.analyser))) return false;
      if (filters.resolution.length && (!an || !filters.resolution.includes(an.resolution))) return false;
      if (filters.tag.length && !filters.tag.every((t) => (p.tags || []).includes(t))) return false;
      return true;
    });
  }, [profiles, filters, search]);

  const counts = useMemo(() => {
    const chromaTotal = profiles.reduce((s, p) => s + (p.chromatograms?.length ?? 0), 0);
    return {
      total: profiles.length,
      chromaTotal,
      orphans: orphans.length,
    };
  }, [profiles, orphans]);

  // F11: real assign — orphan file_upload becomes a chromatogram row
  // pinned to the chosen configuration. Per-row pending state is tracked
  // by file_upload_id so the OrphanStrip can render a brief "Assigning…"
  // affordance on the in-flight row.
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const assignChromatogramM = useMutation({
    mutationFn: ({ configId, fileUploadId }: { configId: string; fileUploadId: string }) =>
      assignChromatogram(configId, fileUploadId),
    onMutate: ({ fileUploadId }) => {
      setAssigningId(fileUploadId);
    },
    onSettled: () => {
      setAssigningId(null);
      void qc.invalidateQueries({ queryKey: instrumentsKeys.orphans });
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    },
  });
  const handleAssignOrphan = useCallback(
    (chId: string, profileId: string) => {
      if (!chId || !profileId) return;
      assignChromatogramM.mutate({ configId: profileId, fileUploadId: chId });
    },
    [assignChromatogramM],
  );

  const handleSaveProfile = (draft: InstrumentProfile, opts: { asNew?: boolean } = {}) => {
    if (opts.asNew) {
      const id = `inst-${Date.now()}`;
      setProfiles((ps) => [
        { ...draft, id, chromatograms: [], alkaneLadder: [], createdAt: "2026-05-26", lastUsedAt: null },
        ...ps,
      ]);
    } else if (drawer?.mode === "edit") {
      setProfiles((ps) => ps.map((p) => (p.id === draft.id ? { ...p, ...draft } : p)));
    } else {
      const id = `inst-${Date.now()}`;
      setProfiles((ps) => [
        { ...draft, id, chromatograms: [], createdAt: "2026-05-26", lastUsedAt: null },
        ...ps,
      ]);
    }
    setDrawer(null);
  };

  // F6 moved archive into the drawer (DeleteConfigModal → archiveM mutation),
  // so this handler is no longer in the click path. The drawer still
  // checks `onDelete` truthiness to decide whether to render the trash
  // icon — passing a no-op keeps that gate intact without rebuilding
  // the local-state-mutation logic the mutation now owns.
  const handleDeleteProfile = (_id: string) => {
    /* delegated to drawer's archiveM */
  };

  return (
    <div
      className="fade-in"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "var(--bg)",
      }}
    >
      {/* Top bar */}
      <div
        className="hstack"
        style={{
          padding: "0 20px",
          minHeight: 57,
          borderBottom: "1px solid var(--line)",
          background: "var(--panel)",
          flexShrink: 0,
          gap: 14,
        }}
      >
        <button className="btn xs ghost" onClick={() => navigate("/flavors/new")} title="Back">
          <Icon name="arrow-l" size={12} />
        </button>
        <div className="hstack gap-3" style={{ flex: 1, minWidth: 0 }}>
          <span className="display" style={{ fontSize: 17, lineHeight: 1 }}>
            Manage Chromatography
          </span>

          <div style={{ width: 24, flexShrink: 0 }} />

          <div className="hstack gap-4" style={{ minWidth: 0, overflow: "hidden" }}>
            <StatTile label="Configs" value={counts.total} />
            <StatTile
              label="Chromatograms"
              value={counts.chromaTotal}
              warning={
                counts.orphans > 0
                  ? `${counts.orphans} need${counts.orphans === 1 ? "s" : ""} attention`
                  : null
              }
            />
          </div>
        </div>

        <div className="hstack gap-2" style={{ flexShrink: 0 }}>
          {catalogsQ.isError && (
            <button
              className="hstack gap-1"
              onClick={() => catalogsQ.refetch()}
              title="Retry loading instrument catalogs"
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                background: "rgba(161, 75, 58, 0.08)",
                color: "#a14b3a",
                border: "1px solid rgba(161, 75, 58, 0.28)",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <Icon name="alert" size={10} />
              Catalogs offline — retry
            </button>
          )}
          <button
            className="btn primary sm"
            onClick={() => setDrawer({ mode: "new" })}
            disabled={catalogsQ.isPending || catalogsQ.isError}
            title={
              catalogsQ.isPending
                ? "Loading instrument catalogs…"
                : catalogsQ.isError
                  ? "Instrument catalogs failed to load — retry above"
                  : undefined
            }
            style={{
              opacity: catalogsQ.isPending || catalogsQ.isError ? 0.55 : 1,
            }}
          >
            <Icon name="plus" size={12} />
            Register configuration
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <InstrumentsFilterRail
          profiles={profiles}
          filters={filters}
          setFilters={setFilters}
        />

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Toolbar */}
          <div
            className="hstack"
            style={{
              padding: "10px 18px",
              borderBottom: "1px solid var(--line)",
              background: "var(--panel)",
              gap: 12,
            }}
          >
            <div style={{ position: "relative", flex: 1, maxWidth: 420 }}>
              <div style={{ position: "absolute", left: 10, top: 8, color: "var(--ink-4)" }}>
                <Icon name="search" size={13} />
              </div>
              <input
                className="input"
                placeholder="Search by alias, model, or notes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 12, padding: "7px 10px 7px 32px" }}
              />
            </div>
            <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
              {filtered.length} of {profiles.length}
            </span>
            <div style={{ flex: 1 }} />

            <ConfigPicker
              profiles={profiles}
              value={defaultId}
              onChange={handleSetDefault}
              label={defaultMutationPending ? "Updating…" : "Default for new uploads"}
              width={170}
              align="right"
            />
          </div>

          {/* List */}
          <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
            <OrphanStrip
              orphans={orphans}
              profiles={profiles}
              onAssign={handleAssignOrphan}
              assigningId={assigningId}
            />

            <div className="vstack" style={{ gap: 14 }}>
              {filtered.map((p) => (
                <InstrumentCard
                  key={p.id}
                  profile={p}
                  isDefault={p.id === defaultId}
                  onEdit={() => setDrawer({ mode: "edit", profile: p })}
                />
              ))}
              {configsQ.isPending && profiles.length === 0 && (
                <div
                  style={{
                    borderRadius: 10,
                    border: "1px solid var(--line)",
                    background: "var(--panel)",
                    padding: "20px 22px",
                    boxShadow: "var(--shadow-1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    opacity: 0.6,
                  }}
                >
                  <div
                    style={{
                      width: 180,
                      height: 14,
                      borderRadius: 3,
                      background: "var(--bg-sunk)",
                    }}
                  />
                  <div
                    style={{
                      width: 280,
                      height: 11,
                      borderRadius: 3,
                      background: "var(--bg-sunk)",
                    }}
                  />
                  <div className="mono" style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 4 }}>
                    Loading configurations…
                  </div>
                </div>
              )}
              {configsQ.isError && (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <div className="hstack" style={{ gap: 6, justifyContent: "center", color: "#a14b3a", fontSize: 12 }}>
                    <Icon name="alert" size={12} />
                    Couldn't load configurations.
                  </div>
                  <button
                    className="btn xs"
                    style={{ marginTop: 8 }}
                    onClick={() => configsQ.refetch()}
                  >
                    Retry
                  </button>
                </div>
              )}
              {filtered.length === 0 && !configsQ.isPending && !configsQ.isError && (
                <div style={{ padding: "80px 20px", textAlign: "center", color: "var(--ink-4)" }}>
                  <div style={{ marginBottom: 10 }}>
                    <Icon name="atom" size={28} />
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
                    {profiles.length === 0
                      ? "No configurations registered yet."
                      : "No configurations match your filters."}
                  </div>
                  <div className="mono" style={{ fontSize: 11, marginTop: 4 }}>
                    {profiles.length === 0
                      ? "Register your first GC-MS or LC-MS configuration to start uploading chromatograms."
                      : "Try clearing filters or search terms."}
                  </div>
                  {profiles.length === 0 && (
                    <button
                      className="btn primary sm"
                      style={{ marginTop: 16 }}
                      onClick={() => setDrawer({ mode: "new" })}
                    >
                      <Icon name="plus" size={12} />
                      Register configuration
                    </button>
                  )}
                </div>
              )}
            </div>
            <div style={{ height: 40 }} />
          </div>
        </div>
      </div>

      {drawer && (
        <InstrumentDrawer
          state={drawer}
          tagPool={tagPool}
          tagPoolMap={tagPoolMap}
          getTagUsage={getTagUsage}
          existingAliases={profiles
            .filter((p) => p.id !== (drawer.mode === "edit" ? drawer.profile.id : null))
            .map((p) => p.alias)}
          onCreateTag={handleCreateTag}
          onRenameTag={handleRenameTag}
          onDeleteTag={handleDeleteTag}
          onClose={() => setDrawer(null)}
          onSave={handleSaveProfile}
          onDelete={
            drawer.mode === "edit"
              ? () => handleDeleteProfile(drawer.profile.id)
              : undefined
          }
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top stats — flat metric tiles, no tab borders.
// ─────────────────────────────────────────────────────────────────────
function StatTile({
  label,
  value,
  warning,
}: {
  label: string;
  value: number | string;
  warning?: string | null;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "0 18px 0 0",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 9,
          color: "var(--ink-4)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div className="hstack gap-2" style={{ alignItems: "baseline" }}>
        <span
          className="mono"
          style={{
            fontSize: 20,
            color: "var(--ink)",
            fontFeatureSettings: "'tnum'",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {value}
        </span>
        {warning && (
          <span
            className="hstack gap-1"
            style={{ fontSize: 10.5, color: "#a14b3a", fontWeight: 500, marginLeft: 2 }}
          >
            <Icon name="alert" size={10} />
            {warning}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Filter rail — visually matches the Ingredient Library sidebar.
// ─────────────────────────────────────────────────────────────────────
function InstrumentsFilterRail({
  profiles,
  filters,
  setFilters,
}: {
  profiles: InstrumentProfile[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}) {
  const { findInstrumentCatalog } = useInstrumentsCatalogs();
  const platforms: InstrumentPlatform[] = ["GC-MS", "LC-MS"];
  const countBy = (pred: (p: InstrumentProfile) => boolean) => profiles.filter(pred).length;

  function toggle<K extends keyof Filters>(key: K, val: Filters[K][number]) {
    setFilters((f) => {
      const list = f[key] as string[];
      const next = list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
      return { ...f, [key]: next as Filters[K] };
    });
  }

  const has = <K extends keyof Filters>(key: K, val: Filters[K][number]) =>
    (filters[key] as string[]).includes(val);

  const anyActive =
    filters.vendor.length ||
    filters.platform.length ||
    filters.analyser.length ||
    filters.resolution.length ||
    filters.tag.length;

  const tagsInUse = useMemo(() => {
    const s = new Set<string>();
    profiles.forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [profiles]);

  return (
    <aside
      style={{
        width: 224,
        flexShrink: 0,
        background: "var(--panel-2)",
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <FilterSection title="Platform" count={profiles.length}>
        {platforms.map((p) => (
          <FacetRow
            key={p}
            label={p}
            count={countBy((pf) => findInstrumentCatalog(pf.instrumentId)?.platform === p)}
            active={has("platform", p)}
            onClick={() => toggle("platform", p)}
          />
        ))}
      </FilterSection>

      <FilterSection title="Vendor">
        {INSTRUMENT_VENDORS.map((v) => (
          <FacetRow
            key={v.id}
            label={v.label}
            accent={vendorBrandAccent(v.id)}
            count={countBy((pf) => findInstrumentCatalog(pf.instrumentId)?.vendor === v.id)}
            active={has("vendor", v.id)}
            onClick={() => toggle("vendor", v.id)}
          />
        ))}
      </FilterSection>

      <FilterSection title="Analyser">
        {ANALYSER_TYPES.map((a) => (
          <FacetRow
            key={a.id}
            label={a.label}
            count={countBy((pf) => findInstrumentCatalog(pf.instrumentId)?.analyser === a.id)}
            active={has("analyser", a.id)}
            onClick={() => toggle("analyser", a.id)}
          />
        ))}
      </FilterSection>

      <FilterSection title="Resolution">
        {(["High-resolution", "Unit-mass"] as AnalyserResolution[]).map((r) => (
          <FacetRow
            key={r}
            label={r === "High-resolution" ? "High Resolution" : "Unit Mass"}
            count={countBy(
              (pf) => findAnalyser(findInstrumentCatalog(pf.instrumentId)?.analyser)?.resolution === r,
            )}
            active={has("resolution", r)}
            onClick={() => toggle("resolution", r)}
          />
        ))}
      </FilterSection>

      {tagsInUse.length > 0 && (
        <FilterSection title="Tags">
          {tagsInUse.map((t) => (
            <FacetRow
              key={t}
              label={t}
              count={countBy((pf) => (pf.tags || []).includes(t))}
              active={has("tag", t)}
              onClick={() => toggle("tag", t)}
            />
          ))}
        </FilterSection>
      )}

      <div style={{ flex: 1 }} />
      <div style={{ padding: 14, borderTop: "1px solid var(--line)" }}>
        <button
          className="btn xs ghost"
          style={{ width: "100%" }}
          disabled={!anyActive}
          onClick={() => setFilters(EMPTY_FILTERS)}
        >
          <Icon name="reset" size={10} />
          Reset filters
        </button>
      </div>
    </aside>
  );
}

function FilterSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: "12px 14px 10px",
        borderBottom: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div
        className="hstack"
        style={{ justifyContent: "space-between", marginBottom: 4, paddingBottom: 4 }}
      >
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "var(--ink-3)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {title}
        </span>
        {typeof count === "number" && (
          <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            {count}
          </span>
        )}
      </div>
      <div className="vstack" style={{ gap: 1 }}>
        {children}
      </div>
    </div>
  );
}

function FacetRow({
  label,
  count,
  active,
  onClick,
  accent,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  accent?: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 6px",
        borderRadius: 4,
        background: active ? "var(--accent-soft)" : "transparent",
        border: `1px solid ${active ? "var(--accent-line)" : "transparent"}`,
        color: active ? "var(--accent-ink)" : "var(--ink-2)",
        textAlign: "left",
        fontSize: 12,
        transition: "background 80ms ease",
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-sunk)";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
    >
      {accent && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: accent,
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: active ? "var(--accent-ink)" : "var(--ink-4)",
          fontFeatureSettings: "'tnum'",
        }}
      >
        {count}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Orphan chromatograms strip
// ─────────────────────────────────────────────────────────────────────
function OrphanStrip({
  orphans,
  profiles,
  onAssign,
  assigningId,
}: {
  orphans: ChromatogramFile[];
  profiles: InstrumentProfile[];
  onAssign: (chId: string, profileId: string) => void;
  assigningId: string | null;
}) {
  if (!orphans.length) return null;
  return (
    <div
      style={{
        background: "#fdf6e5",
        border: "1px solid #e7c97a",
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(124,88,19,0.06)",
      }}
    >
      <div className="hstack" style={{ gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 5,
            flexShrink: 0,
            background: "#f6e2b6",
            color: "#7c5813",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #e7c97a",
          }}
        >
          <Icon name="alert" size={12} />
        </div>
        <div className="vstack" style={{ gap: 1, flex: 1, lineHeight: 1.3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#5b3f0d" }}>
            {orphans.length} chromatogram{orphans.length === 1 ? "" : "s"} need a machine
          </span>
          <span style={{ fontSize: 11, color: "#7c5813" }}>
            These files were uploaded without an instrument attribution. Assign one so N-OS can
            interpret retention times correctly.
          </span>
        </div>
      </div>
      <div className="vstack gap-2">
        {orphans.map((ch) => {
          const isAssigning = assigningId === ch.id;
          return (
            <div
              key={ch.id}
              className="hstack"
              style={{
                gap: 10,
                padding: "8px 10px",
                background: "var(--panel)",
                border: "1px solid #ead194",
                borderRadius: 6,
                opacity: isAssigning ? 0.6 : 1,
              }}
            >
              <Icon name="molecule" size={13} />
              <div className="vstack" style={{ gap: 1, flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ch.name}
                </span>
                <span style={{ fontSize: 10, color: "var(--ink-4)" }}>
                  {ch.ingredient ?? "Not linked to an ingredient"} ·{" "}
                  {(ch.sizeMb ?? 0).toFixed(1)} MB · {relativeDate(ch.uploadedAt)}
                </span>
              </div>
              {isAssigning ? (
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: "var(--ink-3)",
                    padding: "4px 10px",
                    borderRadius: 5,
                    background: "var(--bg-sunk)",
                    border: "1px solid var(--line)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Assigning…
                </span>
              ) : (
                <ConfigPicker
                  profiles={profiles}
                  value={null}
                  onChange={(id) => id && onAssign(ch.id, id)}
                  placeholder="Assign to configuration…"
                  width={220}
                  align="right"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Card — clickable header toggles an expanded body with experiment
// configuration left and files attached (alkane ladder + chromatograms)
// right.
// ─────────────────────────────────────────────────────────────────────
const CHROMA_ROW_H = 50;

function PlatformGlyph({
  platform,
  vendor,
  size = 40,
}: {
  platform: InstrumentPlatform | null | undefined;
  vendor: InstrumentVendorId | null | undefined;
  size?: number;
}) {
  const accent = vendorBrandAccent(vendor);
  const isGC = (platform || "").startsWith("GC");
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 7,
        flexShrink: 0,
        background: "var(--bg-sunk)",
        border: "1px solid var(--line)",
        borderLeft: `3px solid ${accent}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontSize: Math.round(size * 0.34),
        fontWeight: 700,
        color: "var(--ink-2)",
        letterSpacing: "-0.01em",
      }}
    >
      {platform ? (isGC ? "GC" : "LC") : "?"}
    </div>
  );
}

function ResolutionTag({ analyser }: { analyser: AnalyserId | undefined }) {
  const meta = findAnalyser(analyser);
  if (!meta) return null;
  const isHires = meta.resolution === "High-resolution";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 7px",
        borderRadius: 3,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.04em",
        color: isHires ? "var(--accent-ink)" : "var(--ink-2)",
        background: isHires ? "var(--accent-soft)" : "var(--bg-sunk)",
        border: `1px solid ${isHires ? "var(--accent-line)" : "var(--line)"}`,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          background: isHires ? "var(--brand-green)" : "var(--ink-3)",
        }}
      />
      {isHires ? "High Resolution" : "Unit Mass"}
    </span>
  );
}

function FamilyBadge({ family }: { family: string | null | undefined }) {
  if (!family) return null;
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        padding: "2px 6px",
        borderRadius: 3,
        background: "var(--bg-sunk)",
        color: "var(--ink-3)",
        border: "1px solid var(--line)",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {family}
    </span>
  );
}

function MiniChroma({
  id,
  peaks = 30,
  width = 84,
  height = 22,
}: {
  id: string;
  peaks?: number;
  width?: number;
  height?: number;
}) {
  let seed = 1;
  for (let i = 0; i < id.length; i++) seed = (seed * 31 + id.charCodeAt(i)) | 0;
  const rng = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const n = Math.min(peaks, 36);
  const points: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * width;
    const h = Math.pow(rng(), 2.4) * height * 0.92 + height * 0.08;
    points.push([x, height - h]);
  }
  let d = `M 0 ${height}`;
  for (const [x, y] of points) {
    d += ` L ${x.toFixed(1)} ${height} L ${x.toFixed(1)} ${y.toFixed(1)} L ${(x + 0.6).toFixed(1)} ${height}`;
  }
  d += ` L ${width} ${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <path d={d} fill="none" stroke="var(--brand-green)" strokeWidth="0.9" opacity="0.85" />
    </svg>
  );
}

// True for the values the design treats as "no input provided" — null,
// undefined, and the empty string. `0` is a real value and must be kept.
const isEmptyDetail = (v: unknown): boolean =>
  v == null || v === "" || (typeof v === "string" && v.trim() === "");

// Build a KV row from a possibly-empty value + suffix. Returns null when
// the input is empty so the section can be filtered + collapsed.
type DetailRow = { k: string; v: string; hint?: string };
const detailRow = (
  k: string,
  v: unknown,
  suffix = "",
  hint?: string,
): DetailRow | null => {
  if (isEmptyDetail(v)) return null;
  return { k, v: `${v}${suffix}`, hint };
};

function DetailSection({ title, rows }: { title: string; rows: (DetailRow | null)[] }) {
  const live = rows.filter((r): r is DetailRow => r !== null);
  if (live.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <SubHeading>{title}</SubHeading>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
        {live.map((r) => (
          <KV key={r.k} k={r.k} v={r.v} hint={r.hint} />
        ))}
      </div>
    </div>
  );
}

function KV({ k, v, hint }: { k: string; v: string; hint?: string }) {
  return (
    <div className="vstack" style={{ gap: 1, minWidth: 0, lineHeight: 1.3 }}>
      <span
        className="mono"
        style={{
          fontSize: 9,
          color: "var(--ink-4)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {k}
      </span>
      <span
        className="mono"
        title={hint || v}
        style={{
          fontSize: 11.5,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFeatureSettings: "'tnum'",
        }}
      >
        {v}
      </span>
    </div>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 9.5,
        color: "var(--ink-4)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: 6,
        paddingBottom: 4,
        borderBottom: "1px solid var(--line-2)",
      }}
    >
      {children}
    </div>
  );
}

function ChromaRow({
  ch,
  onRemove,
  removing = false,
}: {
  ch: ChromatogramFile;
  onRemove?: () => void;
  removing?: boolean;
}) {
  return (
    <div
      className="hstack"
      style={{
        gap: 10,
        padding: "8px 10px",
        borderBottom: "1px solid var(--line-2)",
        background: "var(--panel)",
        minWidth: 0,
        opacity: removing ? 0.55 : 1,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 4,
          flexShrink: 0,
          background: "var(--bg-sunk)",
          color: "var(--ink-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid var(--line)",
        }}
      >
        <Icon name="molecule" size={12} />
      </div>
      <div className="vstack" style={{ gap: 1, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
        <div className="hstack gap-2" style={{ minWidth: 0 }}>
          <span
            className="mono"
            style={{
              fontSize: 12,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ch.name}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 9,
              padding: "1px 5px",
              borderRadius: 2,
              background: "var(--bg-sunk)",
              color: "var(--ink-3)",
              border: "1px solid var(--line)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            {ch.format}
          </span>
        </div>
        <div className="hstack gap-2" style={{ minWidth: 0 }}>
          {ch.ingredient ? (
            <span
              style={{
                fontSize: 10.5,
                color: "var(--ink-3)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              → <span style={{ color: "var(--ink-2)" }}>{ch.ingredient}</span>
            </span>
          ) : (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: "#7c5813",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Icon name="alert" size={10} />
              Not linked to an ingredient
            </span>
          )}
        </div>
      </div>
      <div className="hstack gap-2" style={{ flexShrink: 0 }}>
        <MiniChroma id={ch.id} peaks={ch.peaks} />
        <div className="vstack" style={{ alignItems: "flex-end", lineHeight: 1.25 }}>
          <span
            className="mono"
            style={{ fontSize: 10.5, color: "var(--ink-2)", fontFeatureSettings: "'tnum'" }}
          >
            {ch.peaks} peaks
          </span>
          <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            {relativeDate(ch.uploadedAt)}
          </span>
        </div>
        {onRemove && (
          <button
            className="btn xs ghost"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={removing}
            title="Detach chromatogram from this configuration"
          >
            <Icon name="x" size={11} />
          </button>
        )}
      </div>
    </div>
  );
}


function InstrumentCard({
  profile,
  isDefault,
  onEdit,
}: {
  profile: InstrumentProfile;
  isDefault: boolean;
  onEdit: () => void;
}) {
  const { findInstrumentCatalog, findColumnFamily } = useInstrumentsCatalogs();
  const inst = findInstrumentCatalog(profile.instrumentId);
  const col = findColumnFamily(profile.column?.id);
  const [open, setOpen] = useState(false);

  // F12: server-side chromatograms attached to this config. Fetched lazily
  // only when the card is expanded so the list endpoint isn't hammered
  // for collapsed cards. The X affordance fires archiveChromatogramM
  // which is the path the F6 ArchiveBlockedModal expects the user to
  // walk through before retrying a config archive.
  const qc = useQueryClient();
  const chromasQ = useQuery({
    queryKey: instrumentsKeys.configChromas(profile.id),
    queryFn: () => listConfigChromatograms(profile.id),
    enabled: open,
    select: (d) => (Array.isArray(d) ? d : []),
  });
  // Server-side alkane ladders, sibling query to chromasQ above. Drives
  // both the row's file-count chip and the editor mounted in the expanded
  // body. Mounting the editor here (instead of forcing the user into the
  // drawer) lets ladder upload happen inline.
  const laddersQ = useQuery({
    queryKey: instrumentsKeys.configLadders(profile.id),
    queryFn: () => listConfigLadders(profile.id),
    enabled: open,
    select: (d) => (Array.isArray(d) ? d : []),
  });
  const serverLadders: LadderRow[] = laddersQ.data ?? [];
  const invalidateLadders = useCallback(() => {
    void qc.invalidateQueries({
      queryKey: instrumentsKeys.configLadders(profile.id),
    });
    // Refresh ladder_count badges on the cards list.
    void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
  }, [qc, profile.id]);
  const [removingChromaId, setRemovingChromaId] = useState<string | null>(null);
  const archiveChromatogramM = useMutation({
    mutationFn: (chromatogramId: string) => archiveChromatogram(chromatogramId),
    onMutate: (chromatogramId) => {
      setRemovingChromaId(chromatogramId);
    },
    onSettled: () => {
      setRemovingChromaId(null);
      void qc.invalidateQueries({
        queryKey: instrumentsKeys.configChromas(profile.id),
      });
      // Refresh the card's chromatogram_count badge.
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    },
  });
  const chromas: ChromatogramFile[] = useMemo(
    () => (chromasQ.data ?? []).map(configChromaToFile),
    [chromasQ.data],
  );
  const chromaIdByFileId = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const r of chromasQ.data ?? []) m.set(r.file_upload_id, r.chromatogram_id);
    return m;
  }, [chromasQ.data]);
  const colorAccent = vendorBrandAccent(inst?.vendor);
  const ionization = profile.ms?.ionization
    ? `${profile.ms.ionization}${profile.ms.energy_eV ? ` · ${profile.ms.energy_eV} eV` : ""}`
    : "—";

  return (
    <div
      className="panel"
      style={{
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: "var(--shadow-1)",
        borderColor: "var(--line)",
      }}
    >
      <div
        onClick={() => setOpen((o) => !o)}
        className="hstack"
        style={{
          padding: "18px 20px",
          gap: 18,
          background: open ? "var(--panel-2)" : "var(--panel)",
          borderBottom: open ? "1px solid var(--line)" : "none",
          cursor: "pointer",
          transition: "background 120ms ease",
        }}
        onMouseEnter={(e) => {
          if (!open) (e.currentTarget as HTMLDivElement).style.background = "var(--bg-sunk)";
        }}
        onMouseLeave={(e) => {
          if (!open) (e.currentTarget as HTMLDivElement).style.background = "var(--panel)";
        }}
      >
        <PlatformGlyph platform={inst?.platform} vendor={inst?.vendor} size={44} />

        <div
          className="vstack"
          style={{ gap: 7, flex: 1, minWidth: 0, lineHeight: 1.25 }}
        >
          <div
            className="hstack gap-2"
            style={{ minWidth: 0, alignItems: "center" }}
          >
            <span
              className="display"
              style={{
                fontSize: 17,
                color: "var(--ink)",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {profile.alias}
            </span>
            {isDefault && (
              <span
                title="Default configuration for new uploads"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent-ink)",
                  flexShrink: 0,
                }}
              >
                <Icon name="star-fill" size={13} />
              </span>
            )}
          </div>

          <div
            className="hstack"
            style={{ minWidth: 0, flexWrap: "nowrap", overflow: "hidden", gap: 8 }}
          >
            <span
              className="mono"
              style={{ fontSize: 11, color: colorAccent, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {inst?.vendor || "—"}
            </span>
            <span style={{ color: "var(--ink-mute)" }}>·</span>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--ink-2)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {inst?.label || "—"}
            </span>
            <FamilyBadge family={inst?.family} />
            <ResolutionTag analyser={inst?.analyser} />
            <span style={{ color: "var(--ink-mute)" }}>·</span>
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--ink-3)", whiteSpace: "nowrap" }}
            >
              {col?.family || "—"}
            </span>
            <span style={{ color: "var(--ink-mute)" }}>·</span>
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--ink-3)", whiteSpace: "nowrap" }}
            >
              {ionization}
            </span>
          </div>

          {(profile.tags || []).length > 0 && (
            <div
              className="hstack"
              style={{ gap: 5, flexWrap: "wrap", marginTop: 1 }}
            >
              {profile.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "var(--accent-soft)",
                    color: "var(--accent-ink)",
                    border: "1px solid var(--accent-line)",
                    fontSize: 10.5,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 999,
                      background: "var(--accent-ink)",
                      opacity: 0.55,
                    }}
                  />
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="hstack" style={{ flexShrink: 0, gap: 12 }}>
          {(() => {
            // Use server-side counts so the chips are accurate even when
            // the card is collapsed (the laddersQ / chromasQ queries are
            // gated on `enabled: open`, so they're empty until the user
            // expands the card).
            const ladderN = open ? serverLadders.length : (profile.ladderCount ?? 0);
            const chromaN = open ? chromas.length : (profile.chromatogramCount ?? 0);
            if (ladderN === 0 && chromaN === 0) return null;
            return (
              <div className="hstack" style={{ gap: 10 }}>
                {ladderN > 0 && (
                  <span
                    title={`${ladderN} alkane ladder file${ladderN === 1 ? "" : "s"}`}
                    className="hstack"
                    style={{
                      gap: 5,
                      padding: "3px 8px",
                      borderRadius: 4,
                      background: "var(--bg-sunk)",
                      border: "1px solid var(--line)",
                      color: "var(--ink-2)",
                    }}
                  >
                    <Icon name="molecule" size={13} />
                    <span
                      className="mono"
                      style={{ fontSize: 11, fontWeight: 600, fontFeatureSettings: "'tnum'" }}
                    >
                      {ladderN}
                    </span>
                  </span>
                )}
                {chromaN > 0 && (
                  <span
                    title={`${chromaN} chromatogram file${chromaN === 1 ? "" : "s"}`}
                    className="hstack"
                    style={{
                      gap: 5,
                      padding: "3px 8px",
                      borderRadius: 4,
                      background: "var(--bg-sunk)",
                      border: "1px solid var(--line)",
                      color: "var(--ink-2)",
                    }}
                  >
                    <Icon name="chromatogram" size={13} />
                    <span
                      className="mono"
                      style={{ fontSize: 11, fontWeight: 600, fontFeatureSettings: "'tnum'" }}
                    >
                      {chromaN}
                    </span>
                  </span>
                )}
              </div>
            );
          })()}
          <button
            className="btn xs"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Icon name="edit" size={11} />
            Edit
          </button>
          <span
            style={{
              display: "inline-flex",
              transform: open ? "rotate(0)" : "rotate(-90deg)",
              transition: "transform 140ms ease",
              color: "var(--ink-4)",
            }}
          >
            <Icon name="chevron" size={13} />
          </span>
        </div>
      </div>

      {open && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0,
            alignItems: "stretch",
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderRight: "1px solid var(--line)",
              background: "var(--panel)",
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--ink-3)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: 12,
                fontWeight: 600,
              }}
            >
              Experiment configuration
            </div>

            {(() => {
              // Per-section render. Each row is null when its underlying
              // value is empty; DetailSection drops nulls and skips the
              // whole section when nothing's left.
              const oven = formatOven(profile.oven);
              const hasOven = !isEmptyDetail(oven) && oven !== "—";
              const hasNotes = !isEmptyDetail(profile.notes);

              const sections = (
                <>
                  <DetailSection
                    title="Column"
                    rows={[
                      detailRow("Family", col?.family, "", col?.phase),
                      detailRow("Polarity", col?.polarity),
                      detailRow("Length", profile.column?.length_m, " m"),
                      detailRow("I.D.", profile.column?.id_mm, " mm"),
                      detailRow("Film", profile.column?.film_um, " µm"),
                    ]}
                  />
                  <DetailSection
                    title="Inlet"
                    rows={[
                      detailRow("Carrier", profile.inlet?.carrier),
                      detailRow("Flow", profile.inlet?.flow_ml_min, " mL/min"),
                      detailRow("Mode", profile.inlet?.mode),
                      detailRow("Volume", profile.inlet?.vol_uL, " µL"),
                      detailRow("Inlet temp", profile.inlet?.temp_C, "°C"),
                    ]}
                  />
                  {hasOven && (
                    <div style={{ marginBottom: 12 }}>
                      <SubHeading>Oven program</SubHeading>
                      <span
                        className="mono"
                        style={{ fontSize: 11.5, color: "var(--ink)", lineHeight: 1.5 }}
                      >
                        {oven}
                      </span>
                    </div>
                  )}
                  <DetailSection
                    title="Mass spectrometer"
                    rows={[
                      detailRow("Ionization", profile.ms?.ionization),
                      detailRow("Energy", profile.ms?.energy_eV, " eV"),
                      detailRow("Source temp", profile.ms?.sourceTemp_C, "°C"),
                      detailRow(
                        "Scan range",
                        profile.ms?.scanLo != null && profile.ms?.scanHi != null
                          ? `m/z ${profile.ms.scanLo}–${profile.ms.scanHi}`
                          : null,
                      ),
                      detailRow("Mode", profile.ms?.mode),
                    ]}
                  />
                  {hasNotes && (
                    <div style={{ marginTop: 12 }}>
                      <SubHeading>Notes</SubHeading>
                      <span style={{ fontSize: 11.5, color: "var(--ink-2)", lineHeight: 1.45 }}>
                        {profile.notes}
                      </span>
                    </div>
                  )}
                </>
              );
              return sections;
            })()}
          </div>

          <div
            style={{
              position: "relative",
              overflow: "hidden",
              // Floor the right column at the height needed to show both
              // sections cleanly when the left column is short: alkane
              // header + dropzone + chroma header + chroma empty state.
              // Putting the floor on this wrapper (rather than on the
              // alkane section) lets the grid row grow naturally so
              // neither section gets clipped by `overflow: hidden`.
              minHeight: 220,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "var(--bg-sunk)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  minHeight: 0,
                  borderBottom: "1px solid var(--line)",
                }}
              >
                <div
                  className="hstack"
                  style={{
                    padding: "10px 14px 8px",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--ink-3)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Alkane ladder
                  </div>
                  <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                    {serverLadders.length} file{serverLadders.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div
                  style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflow: "auto",
                    padding: "8px 12px 12px",
                    background: "var(--panel)",
                    borderTop: "1px solid var(--line)",
                    borderBottom: "1px solid var(--line)",
                  }}
                >
                  <AlkaneLadderEditor
                    mode="config"
                    configId={profile.id}
                    serverLadders={serverLadders}
                    onMutated={invalidateLadders}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  minHeight: chromas.length > 0 ? CHROMA_ROW_H * 3.2 + 36 : "auto",
                }}
              >
                <div
                  className="hstack"
                  style={{
                    padding: "10px 14px 8px",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--ink-3)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Chromatograms attached
                  </div>
                  <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                    {chromas.length} file{chromas.length === 1 ? "" : "s"}
                  </span>
                </div>
                {chromas.length === 0 ? (
                  <div
                    style={{
                      padding: "18px 14px",
                      textAlign: "center",
                      color: "var(--ink-4)",
                    }}
                  >
                    <div className="mono" style={{ fontSize: 11, letterSpacing: "0.04em" }}>
                      No chromatograms registered against this profile yet.
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      flex: "1 1 auto",
                      minHeight: 0,
                      overflow: "auto",
                      background: "var(--panel)",
                      borderTop: "1px solid var(--line)",
                    }}
                  >
                    {chromas.map((ch) => {
                      const chromaId = chromaIdByFileId.get(ch.id);
                      return (
                        <ChromaRow
                          key={ch.id}
                          ch={ch}
                          onRemove={
                            chromaId
                              ? () => archiveChromatogramM.mutate(chromaId)
                              : undefined
                          }
                          removing={
                            chromaId !== undefined && chromaId === removingChromaId
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
